import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, type LucideIcon } from "@fg/icons";
import { cn } from "@/lib/utils";

export type MenuEntry =
    | {
          kind?: "item";
          id: string;
          label: string;
          icon?: LucideIcon;
          /** Tailwind colour class for the icon. */
          iconClass?: string;
          shortcut?: string;
          danger?: boolean;
          disabled?: boolean;
          checked?: boolean;
          onSelect: () => void;
      }
    | { kind: "separator"; id: string }
    | { kind: "header"; id: string; content: ReactNode };

type ItemEntry = Extract<MenuEntry, { onSelect: () => void }>;

export type MenuAnchor =
    | { x: number; y: number }
    | { rect: DOMRect; align?: "start" | "end" };

export function anchorFrom(el: Element, align: "start" | "end" = "start"): MenuAnchor {
    return { rect: el.getBoundingClientRect(), align };
}

const isItem = (e: MenuEntry): e is ItemEntry => e.kind === undefined || e.kind === "item";

export interface MenuProps {
    entries: MenuEntry[];
    anchor: MenuAnchor;
    onClose: () => void;
    label: string;
    /** Highlight the first item on open (keyboard-opened menus). */
    focusFirst?: boolean;
    className?: string;
}

/**
 * Viewport-clamped popup menu (context menus, ⋯ overflow, New, avatar).
 * Arrow keys / Home / End / type-ahead move, Enter/Space activate, Esc or
 * Tab or an outside click close. Rendered in a portal; stops key events
 * from bubbling to list shortcut handlers.
 */
export function Menu({ entries, anchor, onClose, label, focusFirst = false, className }: MenuProps) {
    const ref = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const opener = useRef<HTMLElement | null>(
        document.activeElement instanceof HTMLElement ? document.activeElement : null
    );
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
    const enabled = entries.map((e, i) => (isItem(e) && !e.disabled ? i : -1)).filter((i) => i >= 0);
    const [active, setActive] = useState<number>(focusFirst ? enabled[0] ?? -1 : -1);

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const restoreFocus = useCallback(() => {
        const el = opener.current;
        if (el && document.contains(el)) el.focus({ preventScroll: true });
    }, []);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const { width: w, height: h } = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const m = 8;
        let left: number;
        let top: number;
        if ("rect" in anchor) {
            const r = anchor.rect;
            left = anchor.align === "end" ? r.right - w : r.left;
            top = r.bottom + 4;
            if (top + h > vh - m && r.top - h - 4 >= m) top = r.top - h - 4;
        } else {
            left = anchor.x;
            top = anchor.y;
            if (left + w > vw - m) left = anchor.x - w;
            if (top + h > vh - m) top = anchor.y - h;
        }
        left = Math.min(Math.max(m, left), Math.max(m, vw - w - m));
        top = Math.min(Math.max(m, top), Math.max(m, vh - h - m));
        setPos({ left, top });
    }, [anchor]);

    // Focus the menu (or the highlighted item) once positioned.
    useEffect(() => {
        if (!pos) return;
        if (active >= 0) itemRefs.current[active]?.focus({ preventScroll: false });
        else ref.current?.focus({ preventScroll: true });
    }, [pos, active]);

    // Outside click / resize / scroll elsewhere close the menu.
    useEffect(() => {
        const onDown = (e: PointerEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onCloseRef.current();
        };
        const onScroll = (e: Event) => {
            if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
            onCloseRef.current();
        };
        const onResize = () => onCloseRef.current();
        document.addEventListener("pointerdown", onDown, true);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        window.addEventListener("blur", onResize);
        return () => {
            document.removeEventListener("pointerdown", onDown, true);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
            window.removeEventListener("blur", onResize);
        };
    }, []);

    const select = (entry: ItemEntry) => {
        if (entry.disabled) return;
        restoreFocus();
        onClose();
        entry.onSelect();
    };

    const move = (dir: 1 | -1) => {
        if (enabled.length === 0) return;
        const pos = enabled.indexOf(active);
        const nextPos = pos < 0 ? (dir === 1 ? 0 : enabled.length - 1) : (pos + dir + enabled.length) % enabled.length;
        setActive(enabled[nextPos]!);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        switch (e.key) {
            case "ArrowDown": e.preventDefault(); move(1); return;
            case "ArrowUp": e.preventDefault(); move(-1); return;
            case "Home": e.preventDefault(); if (enabled[0] !== undefined) setActive(enabled[0]); return;
            case "End": e.preventDefault(); if (enabled.length) setActive(enabled[enabled.length - 1]!); return;
            case "Escape": e.preventDefault(); restoreFocus(); onClose(); return;
            case "Tab": e.preventDefault(); restoreFocus(); onClose(); return;
            case "Enter":
            case " ": {
                e.preventDefault();
                const entry = entries[active];
                if (entry && isItem(entry)) select(entry);
                return;
            }
            default:
                if (e.key.length === 1 && /\S/.test(e.key)) {
                    const ch = e.key.toLowerCase();
                    const start = enabled.indexOf(active);
                    for (let k = 1; k <= enabled.length; k++) {
                        const idx = enabled[(start + k + enabled.length) % enabled.length]!;
                        const entry = entries[idx];
                        if (entry && isItem(entry) && entry.label.toLowerCase().startsWith(ch)) {
                            setActive(idx);
                            break;
                        }
                    }
                }
        }
    };

    return createPortal(
        <div
            ref={ref}
            role="menu"
            aria-label={label}
            tabIndex={-1}
            data-drive-overlay=""
            onKeyDown={onKeyDown}
            onContextMenu={(e) => e.preventDefault()}
            style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, visibility: pos ? "visible" : "hidden" }}
            className={cn(
                "drive-pop-in fixed z-[70] max-h-[calc(100vh-16px)] min-w-[13rem] max-w-[calc(100vw-16px)] overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-xl outline-none",
                className
            )}
        >
            {entries.map((entry, i) => {
                if (entry.kind === "separator") return <div key={entry.id} role="separator" className="my-1 h-px bg-border" />;
                if (entry.kind === "header") return <div key={entry.id} className="px-2.5 py-2">{entry.content}</div>;
                const Icon = entry.icon;
                return (
                    <button
                        key={entry.id}
                        ref={(el) => { itemRefs.current[i] = el; }}
                        type="button"
                        role={entry.checked === undefined ? "menuitem" : "menuitemcheckbox"}
                        aria-checked={entry.checked}
                        tabIndex={-1}
                        disabled={entry.disabled}
                        data-active={active === i ? "true" : undefined}
                        onMouseEnter={() => !entry.disabled && setActive(i)}
                        onMouseLeave={() => setActive(-1)}
                        onClick={() => select(entry)}
                        className={cn(
                            "flex w-full items-center gap-3 rounded-md px-2.5 py-1.5 text-left text-sm outline-none transition-colors",
                            "disabled:cursor-default disabled:opacity-40",
                            "data-[active=true]:bg-accent data-[active=true]:text-accent-foreground",
                            entry.danger && "text-destructive data-[active=true]:bg-destructive/10 data-[active=true]:text-destructive"
                        )}
                    >
                        {Icon ? (
                            <Icon className={cn("h-4 w-4 shrink-0", entry.danger ? "text-destructive" : entry.iconClass ?? "text-muted-foreground")} aria-hidden />
                        ) : entry.checked !== undefined ? (
                            <Check className={cn("h-4 w-4 shrink-0", entry.checked ? "opacity-100" : "opacity-0")} aria-hidden />
                        ) : null}
                        <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                        {entry.shortcut ? <span className="shrink-0 pl-4 text-xs text-muted-foreground">{entry.shortcut}</span> : null}
                    </button>
                );
            })}
        </div>,
        document.body
    );
}
